/**
 * Data Hub — the adapter seam.
 *
 * Every tab reads through this interface and nothing else. Phase 1 binds it to
 * `fixtures.ts`; each later phase swaps one method to a real ExecBridge endpoint
 * without any tab component changing.
 *
 * Phase 2 → getRuns / getRun            → GET  /metrics/pipeline/runs
 * Phase 2 → getStagingRows              → GET  /metrics/staging/*
 * Phase 2 → getProductionRows           → GET  /metrics/timeseries
 * Phase 2 → getBenchmarks               → GET  /metrics/benchmarks
 * Phase 2 → getNormalizedMatrix         → GET  /metrics/normalize/*
 * Phase 3 → getQcProbes / getQcReport   → GET  /metrics/qc/*
 * Phase 4 → getCategoryScores           → POST /metrics/scores/category
 * Phase 5 → getSummarizedMatrix         → POST /metrics/summarize/rollup
 * Phase 6 → getRoScores                 → GET  /metrics/ro/scores
 */
import {
  BENCHMARKS,
  CATEGORY_SCORES,
  NORMALIZED_MATRIX,
  PIPELINE_RUNS,
  PRODUCTION_ROWS,
  QC_PROBES,
  QC_REPORTS,
  RO_SCORES,
  STAGING_ROWS,
  STATS,
  SUMMARIZED_MATRIX,
} from "./fixtures";
import type {
  BenchmarkMetric,
  CategoryScores,
  DataHubStats,
  Matrix,
  MatrixQuery,
  PipelineRun,
  ProductionRow,
  QcProbe,
  QcReport,
  RoScores,
  StagingRow,
} from "./types";
import {
  fetchDataHubBenchmarks,
  fetchDataHubCategoryScores,
  fetchDataHubNormalizedMatrix,
  fetchDataHubProductionRows,
  fetchDataHubQcProbes,
  fetchDataHubQcReport,
  fetchDataHubRoScores,
  fetchDataHubRun,
  fetchDataHubRuns,
  fetchDataHubStagingRows,
  fetchDataHubStats,
  fetchDataHubSummarizedMatrix,
} from "../../Store/Reducers/DataHubConsoleSlice";
import type { AppDispatch } from "../../Store/Store";

export type DataHubAdapter = {
  /** True while the adapter is serving fixtures rather than live data. */
  readonly isFixture: boolean;

  getStats(): Promise<DataHubStats>;
  getRuns(): Promise<PipelineRun[]>;
  getRun(runId: string): Promise<PipelineRun | null>;
  getStagingRows(): Promise<StagingRow[]>;
  getProductionRows(): Promise<ProductionRow[]>;
  getQcProbes(): Promise<QcProbe[]>;
  getQcReport(
    runId: string,
    scope: "file" | "production"
  ): Promise<QcReport | null>;
  getBenchmarks(): Promise<BenchmarkMetric[]>;
  getNormalizedMatrix(query?: MatrixQuery): Promise<Matrix>;
  getSummarizedMatrix(query?: MatrixQuery): Promise<Matrix>;
  getCategoryScores(): Promise<CategoryScores>;
  getRoScores(): Promise<RoScores>;
};

/** Small delay so loading states are exercised rather than skipped. */
const settle = <T>(value: T, ms = 120): Promise<T> =>
  new Promise(resolve => setTimeout(() => resolve(value), ms));

export const fixtureAdapter: DataHubAdapter = {
  isFixture: true,

  getStats: () => settle(STATS),
  getRuns: () => settle(PIPELINE_RUNS),
  getRun: runId => settle(PIPELINE_RUNS.find(r => r.id === runId) ?? null),
  getStagingRows: () => settle(STAGING_ROWS, 200),
  getProductionRows: () => settle(PRODUCTION_ROWS, 200),
  getQcProbes: () => settle(QC_PROBES),
  getQcReport: (runId, scope) => {
    const report = QC_REPORTS[runId];
    return settle(report ? { ...report, scope } : null);
  },
  getBenchmarks: () => settle(BENCHMARKS, 180),
  getNormalizedMatrix: () => settle(NORMALIZED_MATRIX, 180),
  getSummarizedMatrix: () => settle(SUMMARIZED_MATRIX, 180),
  getCategoryScores: () => settle(CATEGORY_SCORES, 180),
  getRoScores: () => settle(RO_SCORES, 220),
};

/**
 * Which console tabs are backed by real ExecBridge data.
 *
 * Phase 2 lights up Pipeline Runs. Each later phase adds one entry, and the tab
 * bar badges everything not listed here so nobody mistakes a fixture for live
 * tenant data.
 */
export const LIVE_TABS: ReadonlySet<string> = new Set([
  "runs",
  "staging",
  "production",
  "benchmarked",
  "normalized",
  "summarized",
  "category-scores",
  "ro-scores",
  "qc",
]);

/**
 * Live adapter.
 *
 * Every console surface now reads real ExecBridge data. Failures return
 * EMPTY results rather than fixtures: a tab that cannot load should say so, not
 * quietly show sample numbers that look real (the mistake the stat strip made).
 */
const EMPTY_MATRIX = { metrics: [], rows: [] };

/**
 * The live half reads by dispatching DataHubConsoleSlice thunks, so it is
 * built per store — tabs get it from `useDataHubAdapter()`.
 */
export const createLiveAdapter = (dispatch: AppDispatch): DataHubAdapter => ({
  ...fixtureAdapter,
  isFixture: false,

  // Counts are never faked. If the call fails every stat reads "—" rather than
  // showing sample numbers that contradict the tabs below them.
  getStats: async () => {
    try {
      return await dispatch(fetchDataHubStats()).unwrap();
    } catch {
      return {
        staging: null,
        timeseries: null,
        benchmarks: null,
        summaries: null,
        runs: null,
      };
    }
  },

  getRuns: async () => {
    try {
      return await dispatch(fetchDataHubRuns()).unwrap();
    } catch {
      return [];
    }
  },

  getRun: async runId => {
    try {
      return await dispatch(fetchDataHubRun(runId)).unwrap();
    } catch {
      return null;
    }
  },

  getStagingRows: async () => {
    try {
      return await dispatch(fetchDataHubStagingRows()).unwrap();
    } catch {
      return [];
    }
  },

  getProductionRows: async () => {
    try {
      return await dispatch(fetchDataHubProductionRows()).unwrap();
    } catch {
      return [];
    }
  },

  getBenchmarks: async () => {
    try {
      return await dispatch(fetchDataHubBenchmarks()).unwrap();
    } catch {
      return [];
    }
  },

  getNormalizedMatrix: async (query?: MatrixQuery) => {
    try {
      return await dispatch(fetchDataHubNormalizedMatrix(query)).unwrap();
    } catch {
      return EMPTY_MATRIX;
    }
  },

  getSummarizedMatrix: async (query?: MatrixQuery) => {
    try {
      return await dispatch(fetchDataHubSummarizedMatrix(query)).unwrap();
    } catch {
      return EMPTY_MATRIX;
    }
  },

  getCategoryScores: async () => {
    try {
      return await dispatch(fetchDataHubCategoryScores()).unwrap();
    } catch {
      return {
        saved_at: null,
        category_rows: 0,
        overall_rows: 0,
        composite: null,
        categories: [],
        per_kpi: [],
        trend: [],
        label_drift: null,
      };
    }
  },

  getQcProbes: async () => {
    try {
      return await dispatch(fetchDataHubQcProbes()).unwrap();
    } catch {
      return [];
    }
  },

  getQcReport: async (runId, scope) => {
    try {
      return await dispatch(fetchDataHubQcReport({ scope, runId })).unwrap();
    } catch {
      return null;
    }
  },

  getRoScores: async () => {
    try {
      return await dispatch(fetchDataHubRoScores()).unwrap();
    } catch {
      return {
        rcs: 0,
        ocs: 0,
        rcs_band: "—",
        ocs_band: "—",
        risk_dimensions: [],
        opportunity_dimensions: [],
        accounts: [],
        trend: [],
        deltas: [],
        dimension_deltas: [],
        top_movers: [],
      };
    }
  },
});
